import { useEffect } from 'react'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import useGetAgent from '~/features/agents/hooks/useGetAgent'
import useGetAgentByFriendlyUrl from '~/features/agents/hooks/useGetAgentByFriendlyUrl'
import useGetAgentByLegacyChatbotId from '~/features/agents/hooks/useGetAgentByLegacyChatbotId'
import useGetChatbotSettings from '~/features/agents/hooks/useGetChatbotSettings'
import {
  EMBEDDED_CHAT_KEY,
  EMBEDDED_CHAT_VALUE,
} from '~/features/chatbot/constants'
import getAgentAndChatbotDetails from '~/features/chatbot/hooks/usePopulateChatbot/utils'
import { EMarketingClientPath } from '~/models/api'
import { store } from '~/store'

const usePopulateChatbot = () => {
  const { pathname } = useLocation()
  const { agentId, legacyChatbotId } = useParams()
  const navigate = useNavigate()
  const setChatbot = store((state) => state.setChatbot)
  const [searchParams] = useSearchParams()
  const isEmbedded = searchParams.get(EMBEDDED_CHAT_KEY) === EMBEDDED_CHAT_VALUE

  const {
    data: agentDataByFriendlyUrl,
    isError: isErrorAgentByFriendlyUrl,
    isLoading: isLoadingAgentByFriendlyUrl,
  } = useGetAgentByFriendlyUrl(
    !!agentId || !!legacyChatbotId ? '' : pathname.replaceAll('/', ''),
  )

  // Retrieve agent by agent id
  const {
    data: agentDataById,
    isError: isErrorAgentById,
    isLoading: isLoadingAgentById,
  } = useGetAgent({ agentId: agentId ?? '' })

  // Retrieve agent by legacy chatbot id
  const {
    data: agentDataByLegacyChatbotId,
    isError: isErrorAgentByLegacyChatbotId,
    isLoading: isLoadingAgentByLegacyChatbotId,
  } = useGetAgentByLegacyChatbotId({ legacyChatbotId: legacyChatbotId ?? '' })

  const {
    data: chatbotSettings,
    isLoading: isLoadingChatbotSettings,
    isError: isErrorChatbotSettings,
  } = useGetChatbotSettings(
    agentDataByFriendlyUrl?.data?.id ??
      agentDataById?.data?.id ??
      agentDataByLegacyChatbotId?.data?.id ??
      '',
  )

  // Populate agent and chatbot details by friendly url
  useEffect(() => {
    if (
      !isLoadingAgentByFriendlyUrl &&
      !isErrorAgentByFriendlyUrl &&
      agentDataByFriendlyUrl?.data
    ) {
      const chatbotDetails = getAgentAndChatbotDetails(
        agentDataByFriendlyUrl.data,
      )
      setChatbot(chatbotDetails)
    }
  }, [
    agentDataByFriendlyUrl,
    isLoadingAgentByFriendlyUrl,
    isErrorAgentByFriendlyUrl,
    setChatbot,
  ])

  // Populate agent and chatbot details by agent id
  useEffect(() => {
    if (!isLoadingAgentById && !isErrorAgentById && agentDataById?.data) {
      const chatbotDetails = getAgentAndChatbotDetails(agentDataById.data)
      setChatbot(chatbotDetails)
    }
  }, [agentDataById, isLoadingAgentById, isErrorAgentById, setChatbot])

  // Populate agent and chatbot details by agent id using legacy id
  useEffect(() => {
    if (
      !isLoadingAgentByLegacyChatbotId &&
      !isErrorAgentByLegacyChatbotId &&
      agentDataByLegacyChatbotId?.data
    ) {
      const chatbotDetails = getAgentAndChatbotDetails(
        agentDataByLegacyChatbotId.data,
      )
      setChatbot(chatbotDetails)
    }
  }, [
    agentDataByLegacyChatbotId,
    isLoadingAgentByLegacyChatbotId,
    isErrorAgentByLegacyChatbotId,
    setChatbot,
  ])

  // Populate chatbot layout by retrieving chatbot settings by agent id
  useEffect(() => {
    if (
      !isLoadingChatbotSettings &&
      !isErrorChatbotSettings &&
      chatbotSettings?.data?.layout
    ) {
      setChatbot({ layout: chatbotSettings.data.layout })
    }
  }, [
    chatbotSettings,
    isLoadingChatbotSettings,
    isErrorChatbotSettings,
    setChatbot,
  ])

  useEffect(() => {
    if (
      (isErrorAgentByFriendlyUrl ||
        isErrorAgentById ||
        isErrorChatbotSettings ||
        isErrorAgentByLegacyChatbotId) &&
      !isEmbedded
    ) {
      navigate(EMarketingClientPath.home)
    }
  }, [
    isErrorAgentByFriendlyUrl,
    isErrorAgentById,
    isErrorChatbotSettings,
    isErrorAgentByLegacyChatbotId,
    navigate,
    isEmbedded,
  ])

  return {
    isLoading:
      isLoadingAgentByFriendlyUrl ||
      isLoadingAgentById ||
      isLoadingChatbotSettings ||
      isLoadingAgentByLegacyChatbotId,
    isError:
      isErrorAgentByFriendlyUrl ||
      isErrorAgentById ||
      isErrorChatbotSettings ||
      isErrorAgentByLegacyChatbotId,
  }
}

export default usePopulateChatbot
